import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { submitSampleRequest } from "@/api/FormsApi";
import { globalSearch } from "@/api/GlobalSearchApi";
import Button, { BUTTON_VARIANT } from "@/components/Common/Button";
import Autocomplete from "@/components/Common/Autocomplete";
import { Toaster, showToast, TOAST_TYPE } from "@/components/Common/Toast";
import styles from "./LandingEnquiryForm.module.scss";
import { resolveIcon } from "./iconMap";

/**
 * Landing enquiry form card (Figma hero form: "Sample Details / Contact Details
 * / Request Sample"). Fully config-driven — the field groups, labels, submit
 * text and success message all come from the `form` config object, so the same
 * component serves any landing page.
 *
 * Config shape (config.hero.form):
 * {
 *   groups: [
 *     { label: "Sample Details", fields: [
 *       { name, placeholder, type?: "text"|"email"|"tel", required?,
 *         icon?: <iconMap key> — trailing glyph inside the field }
 *     ]},
 *     ...
 *   ],
 *   consentText?: string,
 *   submitText?: string,
 *   successMessage?: string,
 *   errorMessage?: string,
 * }
 *
 * On submit the form stays mounted: it clears its fields and shows an inline
 * success/error line inside the card (rather than replacing the card), so the
 * user keeps their context. Note `submitSampleRequest` resolves to `false` on
 * failure instead of throwing, so success is determined by its return value.
 */
/**
 * Scopes this form's toasts to its own container, so they cannot double-render
 * alongside the `ToastContainer` that `components/Footer.jsx` mounts on every page.
 * ⚠️ The SAME id must be passed to both `<Toaster>` and every `showToast` call —
 * a mismatch sends the toast to the default container, where nothing renders and
 * nothing errors.
 */
const TOAST_CONTAINER_ID = "lp-enquiry-form";

/**
 * Field renderers this form knows about. An unknown/absent `type` falls through to
 * a plain text input, so a typo degrades to a working field rather than a blank
 * one — but use the constants so it does not come to that.
 */
const FIELD_TYPE = Object.freeze({
  TEL: "tel",
  EMAIL: "email",
  PRODUCT_SEARCH: "product-search",
});

/**
 * Field names that get the product typeahead even when the config does not ask for
 * it by `type`.
 *
 * ⚠️ WHY THIS EXISTS. `cfg = { ...DEFAULT_FORM, ...form }` is a SHALLOW merge, so a
 * config that supplies `groups` REPLACES the default array wholesale — and all six
 * supplier types define their own `HERO_FORM` with a plain `product_name` field.
 * Declaring `type: PRODUCT_SEARCH` only on `DEFAULT_FORM` therefore did nothing:
 * verified by the rendered HTML, which had zero comboboxes.
 *
 * Matching on the field NAME means the typeahead is on by default everywhere the
 * field exists, including any type added later, without editing six configs and
 * without a new type silently losing it. A config can still opt out explicitly with
 * `type: "text"`.
 */
const PRODUCT_SEARCH_FIELDS = new Set(["product_name"]);

/** True when this field should render the catalogue typeahead. */
const isProductSearch = (field) =>
  field.type === FIELD_TYPE.PRODUCT_SEARCH ||
  (!field.type && PRODUCT_SEARCH_FIELDS.has(field.name));

const DEFAULT_FORM = {
  groups: [
    {
      label: "Sample Details",
      fields: [
        {
          name: "product_name",
          placeholder: "Product Name*",
          required: true,
          icon: "fieldGrid",
          // Typeahead against the product catalogue, so the submitted request
          // carries a real `productId` instead of a free-typed string. Same
          // behaviour as the sample-request modal on /product/[singleProduct].
          type: FIELD_TYPE.PRODUCT_SEARCH,
        },
        {
          name: "quantity",
          placeholder: "Quantity Required*",
          required: true,
          icon: "fieldGrid",
        },
        {
          name: "delivery_location",
          placeholder: "Delivery Location*",
          required: true,
          icon: "fieldGrid",
        },
      ],
    },
    {
      label: "Contact Details",
      fields: [
        { name: "full_name", placeholder: "Full Name*", required: true },
        {
          name: "email",
          placeholder: "Email ID*",
          type: "email",
          required: true,
        },
        { name: "company_name", placeholder: "Company Name*", required: true },
        { name: "mobile", placeholder: "Mobile Number", type: "tel" },
      ],
    },
  ],
  consentText: "By submitting, I agree with Terms & Conditions",
  submitText: "Request Sample",
  successMessage:
    "Thanks — we've received your request and will be in touch shortly.",
  errorMessage: "Something went wrong. Please try again.",
};

export default function LandingEnquiryForm({ form }) {
  // Merge over the defaults so a config that omits optional copy (e.g. the
  // success/error feedback text) still gets sensible strings.
  const cfg = { ...DEFAULT_FORM, ...(form || {}) };
  const allFields = cfg.groups.flatMap((g) => g.fields);

  const [values, setValues] = useState(() =>
    Object.fromEntries(allFields.map((f) => [f.name, ""])),
  );
  const [loading, setLoading] = useState(false);
  /**
   * Catalogue id of the product picked from the typeahead, or null when the user
   * typed free text. Held OUTSIDE `values` because it is not a form field: it has
   * no input of its own and must not be swept into validation or the reset map.
   */
  const [productId, setProductId] = useState(null);

  const setField = (name, val) => {
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const requiredNames = allFields.filter((f) => f.required).map((f) => f.name);
  const isValid = requiredNames.every((n) => String(values[n] || "").trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || loading) return;
    setLoading(true);
    // NOTE: submitSampleRequest never throws — it resolves to `false` on both
    // HTTP errors and exceptions, so success must be checked on the result.
    const result = await submitSampleRequest({
      products: [
        {
          productName: values.product_name || "",
          // Set only when the user PICKED a suggestion; null after free typing, so
          // the id can never contradict the name (see the Autocomplete notes).
          // `submitSampleRequest` already forwards this field — the landing form
          // simply never populated it before.
          productId,
          quantity: values.quantity || "",
        },
      ],
      deliveryLocation: values.delivery_location || "",
      fullName: values.full_name || "",
      email: values.email || "",
      companyName: values.company_name || "",
      mobileNumber: values.mobile || "",
    });
    setLoading(false);

    // Feedback is a TOAST rather than an inline line inside the card. The inline
    // version pushed the layout down on success, shifting the whole band while the
    // user was still looking at it.
    //
    // ⚠️ `submitSampleRequest` NEVER THROWS — it returns false on both HTTP failure
    // and caught exceptions. Success must be keyed off the return value; a
    // try/catch around it reports success on failed submissions and loses the
    // user's data.
    if (result) {
      // Keep the form mounted and simply clear it, so the card never goes blank.
      showToast(cfg.successMessage, {
        type: TOAST_TYPE.SUCCESS,
        containerId: TOAST_CONTAINER_ID,
      });
      setValues(Object.fromEntries(allFields.map((f) => [f.name, ""])));
      // Clear the selected product too, or the next submission would reuse the
      // previous id against a newly typed name.
      setProductId(null);
    } else {
      showToast(cfg.errorMessage, {
        type: TOAST_TYPE.ERROR,
        containerId: TOAST_CONTAINER_ID,
      });
    }
  };

  return (
    <form className={styles.card} onSubmit={handleSubmit} noValidate>
      {cfg.groups.map((group, gi) => (
        <div className={styles.group} key={gi}>
          {group.label && <p className={styles.groupLabel}>{group.label}</p>}
          {group.fields.map((field) =>
            field.type === FIELD_TYPE.TEL ? (
              <PhoneInput
                key={field.name}
                country="us"
                enableSearch
                value={values[field.name]}
                onChange={(val) => setField(field.name, val)}
                inputClass={styles.phoneInput}
                buttonClass={styles.phoneButton}
                containerClass={styles.phoneWrap}
                placeholder={field.placeholder}
              />
            ) : isProductSearch(field) ? (
              /*
                Product typeahead against the catalogue, so the request carries a
                real `productId` — the same behaviour as the sample-request modal on
                /product/[singleProduct], via the shared Autocomplete.

                The input keeps this form's own `.input`/`.hasIcon` classes, so it
                is visually identical to every other field; Autocomplete styles only
                the dropdown and spinner.
              */
              <Autocomplete
                key={field.name}
                className={styles.field}
                value={values[field.name]}
                fetcher={globalSearch}
                getOptionLabel={(p) => p.ChemicalName}
                getOptionValue={(p) => p.id}
                noResultsText="No products found"
                onChange={({ label, value }) => {
                  setField(field.name, label);
                  setProductId(value);
                }}
                inputProps={{
                  className: `${styles.input} ${styles.hasIcon}`,
                  name: field.name,
                  placeholder: field.placeholder,
                }}
                // A magnifier, NOT the config's `icon`. This is the only field that
                // keeps a trailing glyph, because here it carries meaning: the input
                // searches. The decorative dot-grid that used to sit on every field
                // is gone — see the note above the plain-input branch.
                trailing={
                  <img
                    className={styles.fieldIcon}
                    src={resolveIcon("search")}
                    alt=""
                    aria-hidden="true"
                  />
                }
              />
            ) : (
              /*
                Plain input — NO trailing icon.

                The configs still set `icon: "fieldGrid"` on 18 fields, but it is
                deliberately ignored here: the glyph was a decorative dot-grid that
                looked like an interactive affordance and did nothing. The config
                key is left in place rather than stripped from six type files, since
                nothing reads it and removing it is churn.
              */
              <span className={styles.field} key={field.name}>
                <input
                  className={styles.input}
                  type={field.type || "text"}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={values[field.name]}
                  onChange={(e) => setField(field.name, e.target.value)}
                />
              </span>
            ),
          )}
        </div>
      ))}

      {cfg.consentText && <p className={styles.consent}>{cfg.consentText}</p>}

      {/*
        Toast container for this form's submit feedback. Mounted HERE rather than
        relying on the one inside `components/Footer.jsx`: react-toastify renders
        nothing at all without a container in the tree and fails silently, so tying
        this form's feedback to whether an unrelated component happens to be on the
        page is a trap. `containerId` scopes it so it cannot double-render
        alongside the Footer's container.
      */}
      <Toaster containerId={TOAST_CONTAINER_ID} enableMultiContainer />

      {/*
        Shared Button, `filled` + `block`. `styles.submit` is retained and now
        carries ONLY this form's two deliberate deviations from the shared variant:
        a 90%-white label (the variant uses 80%) and no border. Both are visible on
        a form submit sitting inside a card, so they are preserved rather than
        normalised — see the note in the stylesheet.

        `loading` drives the spinner and the disabled state, which also prevents the
        double-submit the old markup allowed only via its `disabled` prop.
      */}
      <Button
        type="submit"
        variant={BUTTON_VARIANT.FILLED}
        block
        className={styles.submit}
        disabled={!isValid}
        loading={loading}
      >
        {loading ? "Submitting…" : cfg.submitText}
      </Button>
    </form>
  );
}
